import { useContext, useEffect, useRef, useState } from "react";
import { CustomerService } from "../../../ApplicationCode/Customer/CustomerService";
import { SystemDataLoadingStatus, SystemOperation } from "../../../Model/SystemModels";
import { Container } from "../../Common/Container/Container";
import { SystemCore } from "../../../Core/System/SystemCore";
import { Loader } from "../../Common/Loader/Loader";
import { CustomerInformationContext, ICustomerInformationContext } from "../../../ApplicationCode/Customer/CustomerInformationContext";
import { Customer, CustomerContractResult } from "../../../Model/CustomerModels";
import { MessageType, ToastService } from "../../../Core/Toast/ToastService";
import { CommandBar, CommandBarButton, IButtonProps, ICommandBarItemProps, Stack } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
import { CustomerContractDetail } from "./Components/CustomerContractDetail";
import { CustomerBillingInfoCard } from "../CustomerBillingInfoCard/CustomerBillingInfoCard";
import { PageContainerBase } from "../../Common/Template/PageContainer";
import { t } from "i18next";
import { CommonFunctions } from "../../../ApplicationCode/Common/CommonFunctions";
import { commandBarButtonStyles, lgCommandBarButtonStyles, mdCommandBarButtonStyles, smCommandBarButtonStyles, xlCommandBarButtonStyles, xxlCommandBarButtonStyles } from "./Styles/CustomerContractsStyles";
import { useBreakpoint } from "../../Common/Container/ContainerStyles";
import { BreakpointType } from "../../Common/Container/ContainerFluid";

export const CustomerContracts: React.FC = () => {
    const [dataStatus, setDataStatus] = useState<SystemDataLoadingStatus>(SystemDataLoadingStatus.ToLoad);
    const [contractResults, setContractResults] = useState<JSX.Element[]>([]);
    const billingCardRef = useRef<PageContainerBase>(null);

    const navigate = useNavigate();
    const customerInfoContext: ICustomerInformationContext = useContext<ICustomerInformationContext>(CustomerInformationContext);
    const customerService = new CustomerService();
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    const getCommandBarItems = (): ICommandBarItemProps[] => {
        return (
            [
                {
                    key: 'back',
                    text: t("customerCenter:customerContracts:back")!,
                    iconProps: { iconName: 'Back' },
                    buttonStyles: commandBarButtonStyles,
                    onClick: () => {navigate(-1);}
                }
            ]
        );
    };

    const getCommandBarFarItems = (): ICommandBarItemProps[] => {
        return (
            [
                {
                    key: 'updateStatus',
                    text: t("customerCenter:customerContracts:update")!,
                    iconProps: { iconName: 'Sync' },
                    onClick: () => {getCustomerDigitalContract();}
                }
            ]
        );
    };

    const CustomCommandBarButton: React.FunctionComponent<IButtonProps> = (props) => {
        return (
            <CommandBarButton 
                {...props} 
                styles={
                    isSM ?
                        smCommandBarButtonStyles :
                    isMD ?
                        mdCommandBarButtonStyles :
                    isLG ?
                        lgCommandBarButtonStyles :
                    isXL ?
                        xlCommandBarButtonStyles :
                    isXXL ?
                        xxlCommandBarButtonStyles
                    :
                        commandBarButtonStyles
                }
            />
        );
    };
    
    const checkCustomerCompanyData = (customer: Customer): boolean => {
        let customerData: Object = {};
        
        switch (customer.legalEntityCode) {
            case 'ESS':
                customerData = {
                    companyName: customer.companyName,
                    firstName: customer.firstName,
                    lastName: customer.lastName,
                    phoneNo: customer.phoneNo,
                    streetAddress: customer.streetAddress,
                    zipCode: customer.zipCode,
                    city: customer.city,
                    county: customer.county,
                    country: customer.country,
                    email: customer.email,
                    legalRepresentativeFirstName: customer.legalRepresentativeFirstName,
                    legalRepresentativeLastName: customer.legalRepresentativeLastName,
                };
                break;
            default:
                customerData = {
                    companyName: customer.companyName,
                    firstName: customer.firstName,
                    lastName: customer.lastName,
                    phoneNo: customer.phoneNo,
                    streetAddress: customer.streetAddress,
                    zipCode: customer.zipCode,
                    city: customer.city,
                    county: customer.county,
                    country: customer.country,
                    vat: customer.vat,
                    sdi: customer.sdi,
                    pecEmail: customer.pecEmail,
                    email: customer.email,
                    legalRepresentativeFirstName: customer.legalRepresentativeFirstName,
                    legalRepresentativeLastName: customer.legalRepresentativeLastName,
                };
                break;
        };
                
        let statusValue = true;
        const customerDataKeys: string[] = Object.keys(customerData);
        for(let I = 0; I < customerDataKeys.length - 1; I++) {
            const currentValue: string = Object.values(customerData)[I];
            
            if (!currentValue) {
                statusValue = false;
                break;
            }
        }
        
        return statusValue;
    };
        
    const reloadCustomerBillingInfoCardData = (operationType: SystemOperation, rec: Customer)=>{
        if (!checkCustomerCompanyData(rec)){
            customerInfoContext.reloadCustomer(rec.id).then(() => {
                ToastService.showMessage(MessageType.Error, t('customerCenter:Err00004Text'));
                billingCardRef.current?.handleError();
            })
            .catch((jqXHR) => {
                ToastService.showMessage(MessageType.Error, jqXHR.message);
                billingCardRef.current?.handleError();
            });
        }
        else
        {
            if (!(rec.legalEntityCode === 'ESS')) 
            {
                CommonFunctions.validateVat(rec.vat)
                .then((resp) => {
                    if (resp.risultato === "NON_VALIDA" || resp.risultato === "ERROR")
                    {
                        customerInfoContext.reloadCustomer(rec.id).then(() => {
                            ToastService.showMessage(MessageType.Warning, t('customerCenter:Err00005Text')+": "+rec.vat);
                            billingCardRef.current?.handleError();
                        })
                        .catch((jqXHR) => {
                            ToastService.showMessage(MessageType.Error, jqXHR.message);
                            billingCardRef.current?.handleError();
                        });
                    }
                    else
                        updateCustomer(rec);
                })
                .catch((err) => {
                    customerInfoContext.reloadCustomer(rec.id).then(() => {
                        ToastService.showMessage(MessageType.Error, err.message);
                        billingCardRef.current?.handleError();
                    })
                    .catch((jqXHR) => {
                        ToastService.showMessage(MessageType.Error, jqXHR.message);
                        billingCardRef.current?.handleError();
                    });
                })
            }
            else
                updateCustomer(rec);
        }
    }
    
    const updateCustomer = (rec: Customer) => {
        customerInfoContext.updateCustomer(rec).then(() => {
            customerInfoContext.reloadCustomer(rec.id).then(() => {
                billingCardRef.current?.closePage();
                window.location.reload();
                ToastService.showMessage(MessageType.Success, t('customerCenter:Text00002Lbl'));
            })
            .catch((jqXHR) => {
                ToastService.showMessage(MessageType.Error, jqXHR.message);
                billingCardRef.current?.handleError();
            });
        })
        .catch((jqXHR) => {
            customerInfoContext.reloadCustomer(rec.id).then(() => {
                ToastService.showMessage(MessageType.Error, jqXHR.message);
                billingCardRef.current?.handleError();
            });
        });
    }

    const getCustomerDigitalContract = () => {
        setDataStatus(SystemDataLoadingStatus.Loading);
        customerService.getCustomerDigitalContract(customerInfoContext.customer).then((resp: CustomerContractResult[]) => {
            buildContractDetails(resp);
            setDataStatus(SystemDataLoadingStatus.Loaded);
        })
        .catch((error) => {
            setDataStatus(SystemDataLoadingStatus.Loaded);
            ToastService.showMessage(MessageType.Error, "Error getting contract data: " + error.message);
        });
    };

    const buildContractDetails = (contracts: CustomerContractResult[]) => {
        const contractDetails: JSX.Element[] = [];
        contracts.forEach((contract) => {
            contractDetails.push(
                <CustomerContractDetail
                    key={contract.customerDigitalContract.id}
                    contractData={contract}
                    customer={customerInfoContext.customer}
                    fillCompanyData={() => {
                        ToastService.showMessage(MessageType.Warning, t('customerCenter:Err00003Text'));
                        billingCardRef.current?.addAfterSaveCallback(() => {window.location.replace("/app/customer/contracts");})
                        billingCardRef.current?.setRecord(customerInfoContext.customer);
                        billingCardRef.current?.openPage(SystemOperation.Update);
                    }}
                />
            );
        });
        
        setContractResults(contractDetails);
    };

    useEffect(() => {
        getCustomerDigitalContract();
    }, []);

    if(!SystemCore.isFinishedDataStatus(dataStatus))
        return (
            <Loader 
                text={t("customerCenter:customerContracts:loading")!} 
            />
        );

    return (
        <>
            <Container>
                <CommandBar
                    buttonAs={CustomCommandBarButton}
                    items={getCommandBarItems()}
                    farItems={getCommandBarFarItems()}            
                />
                <Stack>
                    {contractResults}
                </Stack>
            </Container>
            <CustomerBillingInfoCard 
                onSuccess={reloadCustomerBillingInfoCardData} 
                ref={billingCardRef} 
                record={customerInfoContext.customer ? customerInfoContext.customer : new Customer()} 
            />
        </>
    );
};